import { EntityData, FormElement } from '../../lib/types'
import React, { useCallback, useEffect, useState } from 'react'
import ChangeIndicator from './change-indicator'
import { entities as entitiesApi } from '../../lib/api'
import EntitySearch from '../entity-search'
import { DismissibleTag } from '@carbon/react'

type Props = {
    id: number
    element: FormElement
}

export default function MultiEntityElement(props: Props) {
    const { id, element } = props
    const [loaded, setLoaded] = useState(false)
    const [text, setText] = useState('')
    const [entities, setEntities] = useState<Array<EntityData>>([])
    const [changed, setChanged] = useState(false)
    const [err, setErr] = useState('')

    // Load initial data
    useEffect(() => {
        if (loaded) return
        setLoaded(true)
        if (!id) return
        entitiesApi
            .listMulti(id, element.target!, element.entity!)
            .then(setEntities)
            .catch((err) => setErr(err.message || err))
    }, [loaded, id, element])

    const optionClick = useCallback(
        (entity: EntityData) => {
            setText('')
            setChanged(true)
            setErr('')

            let newEntities = entities.concat([entity])
            setEntities(newEntities)
            entitiesApi
                .updateMulti(
                    id,
                    element.target!,
                    newEntities.map((e) => Number(e.id)),
                )
                .catch((err) => setErr(err.message || err))
        },
        [entities, element, id],
    )

    function remove(i: number) {
        setChanged(true)

        let newEntities = entities.slice()
        newEntities.splice(i, 1)
        setEntities(newEntities)
        entitiesApi
            .updateMulti(
                id,
                element.target!,
                newEntities.map((e) => Number(e.id)),
            )
            .catch((err) => setErr(err.message || err))
    }

    return (
        <div className='mt-2'>
            <div className='flex'>
                <label className='font-bold text-blue-700'>
                    {element.label}
                    <ChangeIndicator changed={changed} />
                </label>

                {!!id && (
                    <div className='text-gray-400 text-sm ml-10'>
                        Note: This field is saved as you edit.
                    </div>
                )}
                {!id && (
                    <div className='text-gray-400 text-sm ml-10'>
                        Note: You must save the new row before you can edit this field.
                    </div>
                )}
            </div>
            {err && <div className='text-red-500'>{err}</div>}
            <EntitySearch
                entities={[element.entity!]}
                text={text}
                textChange={setText}
                onErr={setErr}
                optionClick={optionClick}
                readonly={!id || props.element.readonly}
            />

            <div className='mt-3 border border-gray-200 rounded px-2 py-2 flex flex-row flex-wrap gap-2'>
                {!entities.length && <div className='text-gray-500 text-sm'>None Selected</div>}
                {entities.map((e, i) => (
                    <DismissibleTag 
                        key={e.id} 
                        size="md" 
                        text={e.name} 
                        title="Remove"
                        type="blue"
                        onClose={() => remove(i)}
                    />
                ))}
            </div>
        </div>
    )
}
